import { useCallback, useEffect, useState } from 'react';
import { api, ErreurApi } from './api';
import type { ControleAffaire, GesteAffaire, ParcoursAffaire, PieceAffaire } from './api';
import { FilAffaire } from './messages-affaire';

/**
 * LE PARCOURS D'UNE AFFAIRE — de la demande à la remise du titre.
 *
 * ⚠️⚠️ TROIS QUESTIONS, ET L'ÉCRAN DOIT RÉPONDRE AUX TROIS SANS QU'ON CLIQUE :
 *   1. OÙ SUIS-JE dans le parcours ? → la frise, avec les étapes faites,
 *      l'étape courante, et celles à venir ;
 *   2. QU'AI-JE À FAIRE ICI ?        → la consigne de l'étape, écrite pour
 *      l'agent, pas pour l'usager ;
 *   3. QUE PUIS-JE FAIRE ?           → les gestes, nommés en langage métier
 *      (« Transmettre le dossier au Cadastre national »), et ceux qui sont
 *      fermés AVEC leur motif.
 *
 * ⚠️ AUCUNE RÈGLE N'EST ÉVALUÉE ICI. Les libellés, l'ouverture des gestes et
 * les motifs de fermeture viennent du service. Un écran qui recalcule la
 * règle de son côté la recalculera autrement que le suivant — mesuré dans ce
 * dépôt : 10 gestes sur 24 exigeaient une permission qu'aucun rôle ne
 * détenait, et l'écran les affichait quand même.
 *
 * ⚠️ UN GESTE FERMÉ S'AFFICHE, IL NE DISPARAÎT PAS. Un bouton absent se lit
 * « le produit ne fait pas ça » ; un bouton fermé qui dit pourquoi se lit
 * « il me manque le quitus fiscal ». Les deux états sont opposés.
 */
export function ParcoursAffaireVue({ reference, onChange }: {
  reference: string; onChange?: () => void;
}) {
  const [p, setP] = useState<ParcoursAffaire | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState<string | null>(null);
  const [geste, setGeste] = useState<GesteAffaire | null>(null);
  const [motif, setMotif] = useState('');

  const charger = useCallback(async () => {
    setErreur(null);
    try { setP(await api.affaires.parcours(reference)); }
    catch (e) { setErreur(e instanceof ErreurApi ? e.message : 'Parcours illisible'); }
  }, [reference]);

  useEffect(() => { void charger(); }, [charger]);

  async function agir(fn: () => Promise<{ message?: string }>, nom: string) {
    setEnCours(nom); setErreur(null);
    try {
      const r = await fn();
      setGeste(null); setMotif('');
      await charger();
      onChange?.();
      if (r.message) setInfo(r.message);
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : 'Action refusée');
    } finally { setEnCours(null); }
  }
  const [info, setInfo] = useState<string | null>(null);

  if (erreur && !p) return <p className="erreur">{erreur}</p>;
  if (!p) return <p className="aide">Lecture du parcours…</p>;

  const mienne = p.affectation?.c_est_moi === true;

  return (
    <section className="parcours">
      {/* ── OÙ J'EN SUIS ────────────────────────────────────────────────── */}
      <header className="parcours-tete">
        <div>
          <h3>{p.procedure?.libelle ?? p.affaire.type}</h3>
          <p className="aide">{p.procedure?.objet}</p>
        </div>
        {p.ici && (
          <span className="parcours-position">
            Étape {p.ici.rang} sur {p.ici.sur} — <strong>{p.ici.libelle}</strong>
          </span>
        )}
      </header>

      {/* ⚠️ L'AFFECTATION AVANT TOUT LE RESTE. Un agent qui ne sait pas si
          l'affaire est la sienne lit les boutons fermés comme une panne. */}
      <AffectationVue p={p} mienne={mienne} enCours={enCours}
        onPrendre={() => agir(() => api.affaires.prendre(reference), 'prendre')}
        onRendre={(m) => agir(() => api.affaires.rendre(reference, m), 'rendre')}
        onEscalader={(m) => agir(() => api.affaires.escalader(reference, m), 'escalader')} />

      {info && <p className="parcours-info">{info}</p>}
      {erreur && <p className="erreur">{erreur}</p>}

      {/* ⚠️ LE DÉLAI SUSPENDU SE DIT. Sinon l'échéance affichée fait croire à
          un retard que l'agent ne peut pas rattraper : la balle est ailleurs. */}
      {p.delai.suspendu && <p className="parcours-suspendu">⏸ {p.delai.pourquoiSuspendu}</p>}
      {p.complementOuvert && (
        <div className="parcours-complement">
          <strong>Complément demandé le {new Date(p.complementOuvert.demande_le).toLocaleDateString('fr-FR')}</strong>
          <p>{p.complementOuvert.motif}</p>
          {p.complementOuvert.pieces_attendues.length > 0 && (
            <p className="aide">Attendu : {p.complementOuvert.pieces_attendues.join(', ')}</p>
          )}
        </div>
      )}

      {/* ── LA FRISE ────────────────────────────────────────────────────── */}
      <ol className="parcours-frise">
        {p.parcours.map((e) => (
          <li key={e.code} className={`parcours-etape ${e.position}`}>
            <span className="parcours-rang">{e.rang}</span>
            <div className="parcours-corps">
              <div className="parcours-ligne">
                <strong>{e.libelle}</strong>
                {e.mode && <span className="jeton">{e.mode}</span>}
                {e.delai_jours !== null && <span className="aide">{e.delai_jours} j</span>}
              </div>
              <p className="aide">{e.quoi}</p>
              {/* ⚠️ LE FONDEMENT SE MONTRE À TOUTES LES ÉTAPES, pas seulement
                  à la courante : un agent qui instruit doit pouvoir dire sur
                  quel texte repose CHAQUE acte du parcours, y compris ceux
                  déjà faits — c'est ce qu'un contentieux regarde en premier.
                  Et son absence s'écrit, elle ne se tait pas : une étape sans
                  texte qui la fonde n'est pas opposable, et un champ vide se
                  lirait « rien à signaler ». */}
              {e.fondement_legal
                ? <p className="parcours-fondement">§ {e.fondement_legal}</p>
                : <p className="parcours-fondement sans-texte">
                    Aucun texte ne fonde cette étape — elle n’est pas opposable.
                  </p>}
              {/* ⚠️ LA CONSIGNE N'APPARAÎT QU'À L'ÉTAPE COURANTE. Partout
                  ailleurs elle dirait à l'agent de faire ce qui n'est pas
                  encore à lui — ou ce qui est déjà fait. */}
              {e.position === 'courante' && e.consigne && (
                <p className="parcours-consigne">➜ {e.consigne}</p>
              )}
              {e.position === 'courante' && p.ici?.sansConsigne && (
                <p className="parcours-consigne indetermine">{p.ici.sansConsigne}</p>
              )}
            </div>
          </li>
        ))}
      </ol>

      {/* ── CE QUE JE PEUX FAIRE ────────────────────────────────────────── */}
      <h4>Actions</h4>
      {p.gestes.length === 0 ? (
        <p className="indetermine">
          Aucun geste n'est déclaré depuis cette étape. Les affaires antérieures au
          moteur (WF-000) ne circulent plus : elles se closent.
        </p>
      ) : (
        <ul className="parcours-gestes">
          {p.gestes.map((g) => (
            <li key={g.sens} className={g.ouvert ? 'ouvert' : 'ferme'}>
              <button type="button" className="bouton"
                disabled={!g.ouvert || enCours !== null}
                onClick={() => { setGeste(g); setMotif(''); }}>
                {g.geste}
              </button>
              {g.aide && <p className="aide">{g.aide}</p>}
              {/* ⚠️ LE MOTIF ET LE REMÈDE, TOUJOURS LES DEUX. Le motif dit ce
                  qui bloque ; le remède dit quoi faire. Sans le second,
                  l'agent va chercher un administrateur. */}
              {!g.ouvert && g.empechement && (
                <p className="parcours-empechement">
                  <strong>Fermé :</strong> {g.empechement.motif}<br />
                  <em>{g.empechement.remede}</em>
                </p>
              )}
            </li>
          ))}
        </ul>
      )}

      {geste && (
        <form className="parcours-motif" onSubmit={(ev) => {
          ev.preventDefault();
          void agir(() => api.affaires.transition(reference, { sens: geste.sens, motif }), geste.sens);
        }}>
          <label htmlFor="motif-geste">
            <strong>{geste.geste}</strong> — motivez votre décision (elle est consignée au registre)
          </label>
          <textarea id="motif-geste" value={motif} rows={3} minLength={10}
            onChange={(ev) => setMotif(ev.target.value)} required
            placeholder="Ce que vous avez constaté, et pourquoi vous concluez ainsi." />
          <div className="parcours-boutons">
            <button type="submit" className="bouton" disabled={motif.trim().length < 10 || enCours !== null}>
              {enCours ? 'En cours…' : 'Confirmer'}
            </button>
            <button type="button" className="bouton discret" onClick={() => setGeste(null)}>Annuler</button>
          </div>
        </form>
      )}

      <PiecesVue reference={reference} pieces={p.pieces} mienne={mienne} onChange={charger} />
      <ControlesVue controles={p.controles} />
      {/* Le fil vient APRÈS les contrôles : on discute de ce qu'on vient de voir. */}
      <FilAffaire reference={reference} />
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
 * L'AFFECTATION — l'agent ne choisit pas son affaire
 * ═══════════════════════════════════════════════════════════════════════════ */

function AffectationVue({ p, mienne, enCours, onPrendre, onRendre, onEscalader }: {
  p: ParcoursAffaire; mienne: boolean; enCours: string | null;
  onPrendre: () => void; onRendre: (m: string) => void; onEscalader: (m: string) => void;
}) {
  const [ouvert, setOuvert] = useState<'rendre' | 'escalader' | null>(null);
  const [motif, setMotif] = useState('');
  const a = p.affectation;

  if (!a) {
    return (
      <div className="parcours-affectation vide">
        {/* ⚠️ « Affectée à personne » N'EST PAS « libre à prendre ». L'agent ne
            choisit pas : le moteur attribue en FIFO. Le dire évite qu'il
            cherche un bouton qui n'existera jamais. */}
        Cette affaire n'est affectée à personne. Elle est dans la file de son service :
        le moteur l'attribuera à l'agent le moins chargé, la plus ancienne d'abord.
        Vous ne pouvez pas la choisir.
      </div>
    );
  }

  return (
    <div className={`parcours-affectation ${mienne ? 'mienne' : 'autre'}`}>
      <div>
        {mienne
          ? <strong>Cette affaire vous est affectée.</strong>
          : <strong>Affectée à {a.agent}.</strong>}
        {' '}
        <span className="aide">
          {a.issue === 'attribuee' ? 'En attente de prise en charge' : 'Prise en charge'}
          {a.affectee_par === 'createur' && ' · vous l’avez créée'}
          {a.affectee_par === 'escalade' && ' · reçue par escalade'}
        </span>
      </div>
      {mienne && (
        <div className="parcours-boutons">
          {a.issue === 'attribuee' && (
            <button type="button" className="bouton" onClick={onPrendre} disabled={enCours !== null}>
              Prendre en charge
            </button>
          )}
          <button type="button" className="bouton discret" onClick={() => { setOuvert('rendre'); setMotif(''); }}>
            Rendre à la file
          </button>
          <button type="button" className="bouton discret" onClick={() => { setOuvert('escalader'); setMotif(''); }}>
            Escalader au supérieur
          </button>
        </div>
      )}
      {ouvert && (
        <form className="parcours-motif" onSubmit={(ev) => {
          ev.preventDefault();
          (ouvert === 'rendre' ? onRendre : onEscalader)(motif);
          setOuvert(null);
        }}>
          <label htmlFor="motif-aff">
            {ouvert === 'rendre'
              ? "Pourquoi ne traitez-vous pas cette affaire ? Le motif est consigné : il permet à votre chef de service de comprendre pourquoi elle tourne."
              : "Pourquoi escaladez-vous ? Votre supérieur en sera saisi nommément."}
          </label>
          <textarea id="motif-aff" rows={2} value={motif} minLength={10} required
            onChange={(ev) => setMotif(ev.target.value)} />
          <div className="parcours-boutons">
            <button type="submit" className="bouton" disabled={motif.trim().length < 10}>Confirmer</button>
            <button type="button" className="bouton discret" onClick={() => setOuvert(null)}>Annuler</button>
          </div>
        </form>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
 * LES PIÈCES — ce qu'il FAUT, et ce qui EST là
 * ═══════════════════════════════════════════════════════════════════════════ */

const ETIQUETTE_PIECE: Record<PieceAffaire['statut'], string> = {
  attendue: 'Attendue', deposee: 'Déposée, à examiner',
  acceptee: 'Acceptée', refusee: 'Refusée',
};

function PiecesVue({ reference, pieces, mienne, onChange }: {
  reference: string; pieces: ParcoursAffaire['pieces']; mienne: boolean; onChange: () => void;
}) {
  const [motif, setMotif] = useState<Record<string, string>>({});
  if (pieces.liste.length === 0) return null;

  return (
    <div className="parcours-pieces">
      <h4>
        Pièces {pieces.manquantes > 0
          ? <span className="parcours-manque">{pieces.manquantes} obligatoire(s) non acceptée(s)</span>
          : <span className="aide">toutes acceptées</span>}
      </h4>
      <ul>
        {pieces.liste.map((pc) => (
          <li key={pc.code} className={`piece ${pc.statut}`}>
            {/* ⚠️ Une pièce versée hors de la liste d'étape se DIT, elle ne se
                cache pas : la cacher fait verser une seconde fois. */}
            {pc.hors_liste && <span className="etiquette gris">hors liste d’étape</span>}{' '}
            <div className="parcours-ligne">
              <strong>{pc.libelle}</strong>
              {pc.obligatoire && <span className="jeton">obligatoire</span>}
              <span className="aide">{ETIQUETTE_PIECE[pc.statut]}</span>
            </div>
            <p className="aide">{pc.aide}</p>
            {/* ⚠️⚠️ LE FICHIER S'OUVRE AVANT DE SE JUGER. Les deux boutons
                ci-dessous décident du sort d'une pièce ; sans ce lien, ils le
                décidaient sur la foi d'un intitulé. Le service rend le contenu
                sous sa propre autorité (sensibilité, habilitation, journal) —
                l'écran ne fait que donner l'adresse.
                ⚠️ `document_identifiant`, jamais `document_id` : l'UUID n'est
                pas adressable. */}
            {pc.document_identifiant && (
              <p className="aide">
                <a href={api.documents.urlContenu(pc.document_identifiant)}
                   target="_blank" rel="noreferrer">
                  Ouvrir <code>{pc.document_identifiant}</code>
                </a>
                {typeof pc.document_octets === 'number'
                  && <> — {pc.document_type_mime ?? 'type inconnu'}, {pc.document_octets} octets</>}
                {pc.deposee_le && <> — versée le {new Date(pc.deposee_le).toLocaleString('fr-FR')}</>}
              </p>
            )}
            {pc.motif_refus && <p className="parcours-empechement">Refusée : {pc.motif_refus}</p>}
            {mienne && pc.statut === 'deposee' && (
              <div className="parcours-boutons">
                <button type="button" className="bouton"
                  onClick={() => { void api.affaires.statuerPiece(reference, pc.code, 'acceptee').then(onChange); }}>
                  Accepter
                </button>
                <input type="text" placeholder="Motif du refus (10 car. min)"
                  value={motif[pc.code] ?? ''}
                  onChange={(ev) => setMotif({ ...motif, [pc.code]: ev.target.value })} />
                <button type="button" className="bouton discret"
                  disabled={(motif[pc.code] ?? '').trim().length < 10}
                  onClick={() => { void api.affaires.statuerPiece(reference, pc.code, 'refusee', motif[pc.code]).then(onChange); }}>
                  Refuser
                </button>
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
 * LES CONTRÔLES — et le seul qu'on risque de prendre pour un succès
 * ═══════════════════════════════════════════════════════════════════════════ */

/**
 * ⚠️⚠️ `non_evaluable` N'EST PAS UN SUCCÈS, ET L'ÉCRAN DOIT LE HURLER.
 * 14 % des 416 819 contrôles de cette base sont dans cet état. Un contrôle
 * « hypothèque existante » qui n'a rien trouvé PARCE QUE la table est vide se
 * présente exactement comme un contrôle conforme. Le ranger avec les verts
 * ferait valider ce qu'on n'a pas su vérifier.
 */
const ETIQUETTE_CONTROLE: Record<ControleAffaire['resultat'], { texte: string; classe: string }> = {
  conforme:      { texte: 'Conforme',                         classe: 'conforme' },
  anomalie:      { texte: 'Anomalie',                          classe: 'anomalie' },
  non_evaluable: { texte: "N'A PAS PU être vérifié",           classe: 'indetermine' },
  non_joue:      { texte: 'Pas encore joué',                   classe: 'indetermine' },
};

function ControlesVue({ controles }: { controles: ParcoursAffaire['controles'] }) {
  if (controles.liste.length === 0) return null;
  const indecis = controles.liste.filter((c) => c.resultat === 'non_evaluable');

  return (
    <div className="parcours-controles">
      <h4>
        Contrôles {controles.bloquants > 0
          ? <span className="parcours-manque">{controles.bloquants} bloquant(s)</span>
          : <span className="aide">aucun blocage</span>}
      </h4>
      {indecis.length > 0 && (
        <p className="parcours-empechement">
          ⚠️ {indecis.length} contrôle(s) n'ont PAS PU être vérifiés. Ce n'est pas un
          succès : « je n'ai rien trouvé » n'est pas « il n'y a rien ». Ils bloquent au
          même titre qu'une anomalie, et il vous faut les trancher explicitement.
        </p>
      )}
      <ul>
        {controles.liste.map((c) => {
          const e = ETIQUETTE_CONTROLE[c.resultat];
          return (
            <li key={c.regle} className={`controle ${e.classe}`}>
              <div className="parcours-ligne">
                <strong>{c.libelle}</strong>
                <span className={e.classe}>{e.texte}</span>
                {c.bloquant && <span className="jeton">bloquant</span>}
              </div>
              {/* ⚠️ LE CANARI S'AFFICHE. Un contrôle qui a examiné 0 candidat
                  n'a rien prouvé, et c'est la seule façon de le voir. */}
              {c.candidats_examines !== null && (
                <p className="aide">{c.candidats_examines} candidat(s) examiné(s)</p>
              )}
              {c.traite_le && (
                <p className="aide">Tranché par un agent : {c.traite_decision} — {c.traite_motif}</p>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
